/**
 * 2026 縣市長選舉開票中心 — 資料存取層
 *
 * 頁面與元件一律透過本檔取數，不直接呼叫 axios。
 * 資料格式定義見 docs/election2026/API_CONTRACT.md
 *
 * ─────────────────────────────────────────────────────────────
 * Endpoint
 * ─────────────────────────────────────────────────────────────
 *
 *   GET ${BASE_URL}/election/2026/results         即時開票（契約 §3）
 *   GET ${BASE_URL}/election/2026/last-election   2022 上屆結果（契約 §8）
 *   GET ${BASE_URL}/topic/275                     相關新聞，主站專題（契約 §7）
 *   GET ${BASE_URL}/topic/275/articles?page=N     相關新聞下一頁
 *
 * BASE_URL 已包含 `/api/v1`（見 package.json scripts），此處只接後段路徑。
 * 兩支開票 API 直接回傳 payload 本體，**沒有**包主站其他 API 慣用的
 * `{ code, message, data }` 外層，取值時不要再 `.data`。
 * 專題 API 是主站既有 API，**有** `data` 外層。
 *
 * ─────────────────────────────────────────────────────────────
 * SSR 快取
 * ─────────────────────────────────────────────────────────────
 *
 * 開票夜流量為平常數十倍，若每個 SSR request 都回源，後端會被打爆。
 * server 端以 module 層級的記憶體快取共用結果（Node process 內跨 request 共享）：
 *
 * - results       快取 5 秒
 * - last-election 快取 10 分鐘（2022 資料不會再變動）
 * - 專題新聞      快取 60 秒（只快取專題首頁，分頁不快取）
 *
 * 另有兩個防護：
 * - 同一時間只會有一個回源請求，快取過期瞬間湧入的 request 共用同一個 Promise，
 *   避免 thundering herd
 * - 回源失敗時若有舊資料，回傳舊資料而非讓整頁 500（開票夜寧可慢 30 秒也不要掛）
 *
 * client 端不走快取 —— 輪詢本來就要拿最新的資料。
 *
 * ─────────────────────────────────────────────────────────────
 * Mock 預覽
 * ─────────────────────────────────────────────────────────────
 *
 * 真實 API 在 10/23 號次抽籤前候選人為空、開票前票數為 0，
 * 無法預覽開票夜的畫面。非正式環境可用 `?mock=live|preheat|result`
 * 改用假資料預覽（見 getMockPhase）。**正式環境一律忽略此參數** ——
 * 選舉期間假票數若被截圖流傳，後果嚴重。
 */

import { COUNTY_MAP, NEWS_TOPIC_ID, POLL_INTERVAL } from './constants'
import { buildMockResults } from './mock'

const RESULTS_PATH = '/election/2026/results'
const LAST_ELECTION_PATH = '/election/2026/last-election'
const TOPIC_PATH = `/topic/${NEWS_TOPIC_ID}`

const RESULTS_TTL = 5 * 1000
const LAST_ELECTION_TTL = 10 * 60 * 1000
const NEWS_TTL = 60 * 1000

const MOCK_PHASES = ['preheat', 'live', 'result']

/**
 * 建立 server 端記憶體快取
 *
 * 快取內容以 JSON 字串保存，每次取用都 parse 出新物件：
 * Vue 2 SSR 會對 asyncData 回傳的物件掛上 reactive observer，
 * 若多個 request 共用同一個物件參照，會互相污染。
 *
 * @param {number} ttl 快取有效毫秒數
 * @returns {function} (loader) => Promise<object>
 */
function createServerCache(ttl) {
  let entry = null
  let inflight = null

  return async function cached(loader) {
    if (entry && Date.now() < entry.expiresAt) {
      return JSON.parse(entry.json)
    }

    if (!inflight) {
      inflight = loader()
        .then((data) => {
          entry = { json: JSON.stringify(data), expiresAt: Date.now() + ttl }
          return data
        })
        .finally(() => {
          inflight = null
        })
    }

    try {
      await inflight
      return JSON.parse(entry.json)
    } catch (err) {
      // 回源失敗但手上有舊資料：寧可顯示稍舊的數字，也不要整頁掛掉
      if (entry) {
        console.error('[election2026] 回源失敗，暫以快取資料回應:', err.message)
        return JSON.parse(entry.json)
      }
      throw err
    }
  }
}

const resultsCache = createServerCache(RESULTS_TTL)
const lastElectionCache = createServerCache(LAST_ELECTION_TTL)
const newsCache = createServerCache(NEWS_TTL)

/**
 * 在 server 端走快取，client 端直接回源
 */
function load(cache, loader) {
  return process.server ? cache(loader) : loader()
}

/**
 * 正規化單一縣市
 *
 * - 縣市名稱改用前端對照表（constants.js）：API 用官方字「臺北市」，
 *   前端顯示統一為「台北市」—— 使用者搜尋的是「台北市長 開票」，
 *   且頁面 title 也用對照表，同一頁不可混用兩種寫法（見契約 §6）
 * - 候選人名單公告前 API 給空陣列，此處確保一定是陣列
 *
 * 候選人姓名不做任何轉換，人名不可改字。
 */
function normalizeCounty(county) {
  const meta = COUNTY_MAP[county.slug]
  return Object.assign({}, county, {
    name: meta ? meta.name : county.name,
    shortName: meta ? meta.shortName : county.shortName,
    candidates: Array.isArray(county.candidates) ? county.candidates : []
  })
}

function normalizePayload(payload) {
  return Object.assign({}, payload, {
    counties: Array.isArray(payload.counties) ? payload.counties.map(normalizeCounty) : []
  })
}

/**
 * 解析 mock 預覽參數
 *
 * @param {object} query 路由 query（asyncData 的 `query` 或 `this.$route.query`）
 * @param {object} config Nuxt runtime config（`$config` 或 `this.$config`）
 * @returns {string|null} mock 的 phase；正式環境或參數無效時為 null
 */
export function getMockPhase(query, config) {
  if (!config || config.mode === 'production') {
    return null
  }
  const phase = query && query.mock
  return MOCK_PHASES.includes(phase) ? phase : null
}

/**
 * 取得即時開票資料（契約 §3）
 *
 * @param {object} $axios Nuxt 注入的 axios 實例
 * @param {object} [options]
 * @param {string|null} [options.mock] 由 getMockPhase 取得，有值時改用假資料
 * @returns {Promise<object>} results payload
 */
export async function fetchResults($axios, options) {
  const settings = options || {}

  if (settings.mock) {
    return await Promise.resolve(normalizePayload(buildMockResults({ phase: settings.mock })))
  }

  return await load(resultsCache, () => $axios.$get(RESULTS_PATH).then(normalizePayload))
}

/**
 * 取得 2022 上屆結果（契約 §8）
 *
 * 靜態資料，client 端不輪詢。mock 預覽時同樣使用真實 API ——
 * 2022 是既成事實，沒有理由用假資料。
 *
 * @param {object} $axios Nuxt 注入的 axios 實例
 * @returns {Promise<object>} last-election payload
 */
export async function fetchLastElection($axios) {
  return await load(lastElectionCache, () => $axios.$get(LAST_ELECTION_PATH).then(normalizePayload))
}

/**
 * 主站文章 → 新聞列表項目
 *
 * 主站文章 API 欄位為 snake_case，在此轉成元件使用的格式，
 * 元件不直接依賴主站欄位命名。
 */
function normalizeArticle(article) {
  return {
    id: article.id,
    title: article.title,
    category: article.main_category || '',
    cover: article.cover || '',
    caption: article.caption || article.title,
    // 顯示用（「2026-10-04 15:57」，後端已是台灣時間）
    publishAt: article.publish_at || '',
    // <time datetime> 用（ISO 8601，含 +08:00）
    publishAtIso: article.publish_at_time_zone || '',
    url: `/article/${article.id}/`
  }
}

function normalizeArticles(articles) {
  return Array.isArray(articles) ? articles.map(normalizeArticle) : []
}

/**
 * 取得相關新聞（專題最新文章）
 *
 * 來源為主站專題 `GET ${BASE_URL}/topic/{NEWS_TOPIC_ID}`，回傳該專題最新的 7 篇文章。
 * 與開票 API 不同，主站 API **有** `{ code, message, data }` 外層。
 *
 * 專題沒有依縣市篩選的參數，主頁與 22 縣市頁顯示同一份清單。
 *
 * server 端快取 60 秒：開票夜 23 頁的每個 SSR request 都會用到，
 * 編輯台掛入新文章晚 1 分鐘出現可以接受。
 *
 * @param {object} $axios Nuxt 注入的 axios 實例
 * @param {object} [options]
 * @param {number} [options.limit] 取幾則，不傳則回傳全部（最多 7 則）
 * @param {string|null} [options.mock] getMockPhase 的結果。mock 預覽一律使用假新聞：
 *   beta 沒有此專題，且假新聞會每 30 秒多一則，才能預覽 Mobile 的最新新聞提示
 * @returns {Promise<array>} 新聞陣列
 */
export async function fetchRelatedNews($axios, options) {
  const settings = options || {}

  if (settings.mock) {
    return await Promise.resolve(buildMockNews(settings.limit || 7))
  }

  const items = await load(newsCache, () => $axios.$get(TOPIC_PATH)
    .then(res => normalizeArticles(res && res.data && res.data.articles)))
  return settings.limit ? items.slice(0, settings.limit) : items
}

/**
 * 取得專題文章的下一頁（新聞列表頁「看更多」用，僅 client 端呼叫）
 *
 * `GET ${BASE_URL}/topic/{NEWS_TOPIC_ID}/articles?page={page}`，每頁 6 篇。
 * page=1 從專題首頁 7 篇**之後**接續（與主站 pages/topic/_id.vue 的分頁方式相同）。
 *
 * 首頁 7 篇有 server 快取、分頁沒有，若期間有新文章掛入，
 * 兩者交界可能重複一篇，呼叫端需依 id 去重。
 *
 * @param {object} $axios Nuxt 注入的 axios 實例
 * @param {number} page 頁碼，從 1 開始
 * @returns {Promise<{ items: array, currentPage: number, lastPage: number }>}
 */
export async function fetchMoreNews($axios, page) {
  const res = await $axios.$get(`${TOPIC_PATH}/articles`, { params: { page } })
  const data = (res && res.data) || {}
  return {
    items: normalizeArticles(data.items),
    currentPage: data.current_page || page,
    lastPage: data.last_page || 0
  }
}

/**
 * 相關新聞假資料，僅 mock 預覽使用
 *
 * 最前面 3 則以輪詢間隔（30 秒）為單位產生，每過 30 秒就多一則新的「最新》」新聞，
 * 用來預覽 Mobile 的最新新聞提示；其後接固定的一般新聞。
 *
 * ⚠️ 全部為虛構標題，連結不存在。
 */
const MOCK_LIVE_TITLES = [
  '桃園市開票率突破五成 兩陣營差距縮小至 4 千票',
  '新竹市三腳督開票率 78% 領先者拉開 6 千票差距',
  '新北市候選人自行宣布當選 對手尚未發表敗選感言',
  '台中市開票過半 現任市長連任之路穩健',
  '高雄市開票率突破七成 綠營確定守住南台灣',
  '花蓮縣開票進度落後 選務人員說明人工計票需時較久'
]

const MOCK_GENERAL_NEWS = [
  { category: '政治', title: '六都選情》三都戰況緊繃 開票率過半仍難分勝負' },
  { category: '政治', title: '首投族湧現 全國投票率 68.4% 較上屆增 2.1 個百分點' },
  { category: '社會', title: '各地開票所外群眾聚集 警方加強維安勤務' },
  { category: '政治', title: '中選會：開票結果以官方系統為準 籲勿輕信未證實消息' },
  { category: '財經', title: '選後政策風向轉變 分析師點名三大內需族群' },
  { category: '政治', title: '小黨版圖鬆動 地方議會席次成關鍵變數' }
]

const TAIWAN_OFFSET = 8 * 60 * 60 * 1000

/**
 * 時間戳 → { publishAt: '2026-11-28 20:36', publishAtIso: '2026-11-28T20:36:00+08:00' }
 * 與主站文章 API 的格式相同，且不受 server 時區影響
 */
function toTaiwanTime(timestamp) {
  const iso = new Date(timestamp + TAIWAN_OFFSET).toISOString()
  return {
    publishAt: iso.slice(0, 10) + ' ' + iso.slice(11, 16),
    publishAtIso: iso.slice(0, 16) + ':00+08:00'
  }
}

function buildMockNews(limit) {
  const tick = Math.floor(Date.now() / POLL_INTERVAL)

  const live = [0, 1, 2].map((offset) => {
    const index = tick - offset
    return Object.assign({
      id: 990000 + (index % 10000),
      title: '最新》' + MOCK_LIVE_TITLES[index % MOCK_LIVE_TITLES.length],
      category: '政治'
    }, toTaiwanTime(index * POLL_INTERVAL))
  })

  // 一般新聞固定排在 1 小時前之後，每則間隔 7 分鐘
  const general = MOCK_GENERAL_NEWS.map((item, index) => Object.assign({
    id: 900000 + index,
    title: item.title,
    category: item.category
  }, toTaiwanTime((tick - 120 - index * 14) * POLL_INTERVAL)))

  return live.concat(general).slice(0, limit).map(item => Object.assign(item, {
    cover: '',
    caption: item.title,
    url: '/article/' + item.id + '/'
  }))
}
